import React from 'react';
import dayjs from 'dayjs';

import { useGetTotalRevenueReportByIdQuery } from '@/store/services/reports';
import { DetailSection } from '../SharedComponents/DetailsSection';
import UserInfo from '../SharedComponents/UserInfo';
import ReportDrawer from '../SharedComponents/ReportDrawer';
import { usePublicState } from '@/store/slices/publicSlice';
import Helpers from '@/lib/helpers';
import { useTranslation } from 'react-i18next';

export interface RequestInfo {
  transactionId: string;
  serial: number;
  requestBudget: number;
  requestCommission: number;
  requestDate: string;
  requestType: string;
  orderId: string | null;
}

export interface AdditionalInfo {
  referenceNo: string | null;
  buyerType: string | null;
}

export interface SellerInfo {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  image?: string;
  countryCode?: string;
  [key: string]: unknown;
}

export interface TotalRevenueDetailsType {
  requestInfo: RequestInfo;
  addInfo: AdditionalInfo;
  sellerInfo: SellerInfo | null;
}

interface RequestDetailsDrawerProps {
  listOfIds: string[];
}

export const RequestDetailsDrawer: React.FC<RequestDetailsDrawerProps> = ({
  listOfIds,
}) => {
  const { modalId } = usePublicState();
  const { t } = useTranslation();

  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(modalId));

  const { data: reportDetails, isFetching } = useGetTotalRevenueReportByIdQuery(
    modalId as string,
    { skip: !isDrawerOpen }
  );

  const response = reportDetails?.payload;
  const seller = response?.sellerInfo;

  const requestInfoItems = [
    {
      label: t('reports.transactionId', 'Transaction ID'),
      value: response?.requestInfo?.serial || '-',
    },
    {
      label: t('reports.requestBudget', 'Request budget'),
      value: `${Helpers.formatMoney(response?.requestInfo.requestBudget || 0)} ${t('components.SAR')}`,
    },
    {
      label: t('reports.requestCommission', 'Request commission'),
      value: `${Helpers.formatMoney(response?.requestInfo.requestCommission || 0)} ${t('components.SAR')}`,
    },
    {
      label: t('reports.requestType', 'Request type'),
      value: response?.requestInfo?.requestType || '-',
    },
    {
      label: t('reports.requestDate', 'Request date'),
      value:
        response?.requestInfo?.requestDate &&
        dayjs(response.requestInfo.requestDate).isValid()
          ? dayjs(response.requestInfo.requestDate).format('DD MMM YYYY')
          : '-',
    },
  ];

  const additionalInfoItems = [
    {
      label: t('reports.paymentReference', 'Payment reference'),
      value: response?.addInfo?.referenceNo || '-',
    },
    {
      label: t('reports.buyerType', 'Buyer type'),
      value: response?.addInfo?.buyerType,
    },
  ];

  return (
    <ReportDrawer
      title={t('reports.requestDetails', 'Request details')}
      listOfIds={listOfIds}
    >
      <div className="space-y-6">
        {/* Request Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.requestInformation', 'Request information')}
          items={requestInfoItems}
        />

        {/* Additional Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.additionalInformation', 'Additional information')}
          items={additionalInfoItems?.filter((i) => !!i.value)}
        />

        {/* Seller Information Section */}
        {(seller?.name || seller?.email || seller?.image) && (
          <UserInfo
            loading={isFetching}
            sectionTitle={t('reports.sellerInformation', 'Seller information')}
            name={seller?.name || ''}
            email={seller?.email || ''}
            phone={seller?.phone || ''}
            image={seller?.image || ''}
            countryCode={seller?.countryCode || ''}
          />
        )}
      </div>
    </ReportDrawer>
  );
};
